/** @format */
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { WrappedLink } from '@toucan/client/global/nextjs';
import { StockAlertPageType } from '@toucan/client/global/product';

import { alertHelper } from '@toucan/client/blocks/hooks/use-alert';

import { DEFAULT_TEST_ID } from './constants';
import { styles } from './product-out-of-stock.styles';
import { ProductOutOfStockProps } from './types';

const StyledProductOutOfStock = styled.div`
    ${styles}
`;

export const ProductOutOfStock: FunctionComponent<ProductOutOfStockProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    productData,
    stockInfo,
    parentCategory,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { handleOpenCreateAlertModal } = alertHelper(
        {
            id: productData.modelId,
            name: productData.name,
        },
        StockAlertPageType.PRODUCT
    );

    function getProductOutOfStockDescription() {
        if (!stockInfo) {
            return null;
        }
        const { priceMax, priceMin, productCount } = stockInfo;

        if (productCount === 0) {
            return null;
        }

        if (priceMin !== priceMax) {
            return t(
                '{{productCount}} more available from {{priceMin}} - {{priceMax}}',
                {
                    priceMin,
                    priceMax,
                    productCount,
                }
            );
        }

        return t('{{productCount}} available at {{priceMin}}', {
            priceMin,
            productCount,
        });
    }

    const outOfStockDescription = getProductOutOfStockDescription();

    return (
        <StyledProductOutOfStock {...otherProps} data-testid={dataTestId}>
            <strong>{t('Someone just bought this product!')}</strong>
            {outOfStockDescription && (
                <WrappedLink
                    href={parentCategory?.templatePath ?? ''}
                    as={parentCategory?.href}
                    className="more-in-stock-link"
                    data-testid={`${dataTestId}__more-in-stock-link`}
                >
                    {outOfStockDescription}
                </WrappedLink>
            )}
            <p>{t('Do you want to hear when more become available?')}</p>
            <Button
                className="create-alert-button"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.SECONDARY}
                onClick={() => handleOpenCreateAlertModal()}
                data-testid={`${dataTestId}__create-alert-button`}
            >
                {t('Create an alert')}
            </Button>
        </StyledProductOutOfStock>
    );
};
